import * as React from "react";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IDefaultViewProps } from "./IDefaultViewProps";
import { Caption } from "../../atoms/views/Caption";
import { ContactpointIcon } from "../../atoms/icons/ContactpointIcon";
import { Table, TableBody, TableCell, TableHead, TableRow } from "@material-ui/core";
import { useTranslation } from 'react-i18next';
import { IApplicationState, RootActionCreators } from "../../../store";
import { FetchState } from "../../../entities/FetchState";

// All the incoming values
interface IProps {
    cityId?: string | null;
    hideIcon?: boolean;
    hideLabel?: boolean;
}

// Outgoing functions
interface IDispatch {
}

// Actual Component
export type AllProps = IProps & IDispatch & Omit<IDefaultViewProps<any>, 'value'>;
export const Contactpunten = (props: AllProps) => {
    const { t: tClient } = useTranslation('client');
    const dispatch = useDispatch();
    
    // Get contactpoints from the store
    const contactpointsState = useSelector((state: IApplicationState) => 
        state.contactpoint.contactpointListByCity.ContactpointListByCity
    );
    const currentCityStreamId = useSelector((state: IApplicationState) => 
        state.contactpoint.contactpointListByCity.currentCityStreamId
    );
    
    // Fetch contactpoints when city ID changes
    useEffect(() => {
        if (props.cityId && props.cityId !== currentCityStreamId) {
            dispatch(RootActionCreators.Contactpoint.contactpointListByCity.FetchData(props.cityId));
        }
    }, [props.cityId, currentCityStreamId, dispatch]);
    
    // Handle row click to navigate to contactpoint detail
    const handleRowClick = (contactpointId: string) => {
        dispatch(RootActionCreators.Navigate.ToContactpointDetail(contactpointId));
    };
    
    const label = props.hideLabel ? undefined : (props.label || tClient('contactpuntenLabel'));
    const icon = props.hideIcon ? undefined : <ContactpointIcon />;
    
    // Determine loading state
    const isLoading = props.loading || contactpointsState.state === FetchState.Busy;
    
    // If no city ID provided
    if (!props.cityId) {
        return (<Caption loading={isLoading} caption={label} icon={icon}>
            {tClient('contactpuntenNoCitySelected')}
        </Caption>);
    }
    
    // If error state
    if (contactpointsState.state === FetchState.Error) {
        return (<Caption loading={false} caption={label} icon={icon}>
            {contactpointsState.errorMessage || tClient('contactpuntenError')}
        </Caption>);
    }
    
    // If no data or empty
    if (!contactpointsState.data || contactpointsState.data.length === 0) {
        if (isLoading) {
            return (<Caption loading={true} caption={label} icon={icon}>
                {tClient('contactpuntenLoading')}
            </Caption>);
        }
        return (<Caption loading={false} caption={label} icon={icon}>
            {tClient('contactpuntenEmpty')}
        </Caption>);
    }
    
    // Show data
    return (
        <Caption loading={isLoading} caption={label} icon={icon}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>{tClient('contactpuntNaam')}</TableCell>
                        <TableCell>{tClient('contactpuntAdres')}</TableCell>
                        <TableCell>{tClient('contactpuntTelefoon')}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {contactpointsState.data.map((contactpunt) => (
                        <TableRow 
                            key={contactpunt.id}
                            onClick={() => handleRowClick(contactpunt.id)}
                            style={{ cursor: 'pointer' }}
                            hover
                        >
                            <TableCell>{contactpunt.naam}</TableCell>
                            <TableCell>
                                {contactpunt.adress && contactpunt.city 
                                    ? `${contactpunt.adress}, ${contactpunt.city}`
                                    : contactpunt.adress || contactpunt.city || '-'
                                }
                            </TableCell>
                            <TableCell>{contactpunt.telefoon || '-'}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Caption>
    );
}

